import type { CaseItem } from "@/components/site/case/CaseCard";

export interface CaseDetail extends CaseItem {
  slug: string;
  /** Metabeskrivning för /case/<slug>. Faller tillbaka på excerpt. */
  seoDescription?: string;
  year?: string;
  scope?: string[];
  challenge?: string;
  solution?: string;
  outcome?: string;
  metrics?: { value: string; label: string }[];
  quote?: { text: string; name: string; role: string };
  gallery?: string[]; // additional youtube ids
}

export const featuredCase: CaseDetail = {
  id: "9iiyNS4yNzA",
  slug: "garageportexperten-maria",
  title: "Maria på Garageportexperten",
  client: "Garageportexperten",
  category: "Case",
  service: "Prime",
  format: "Brand storytelling · 16:9",
  industry: "Industri",
  year: "2025",
  metric: { value: "+38%", label: "rekrytering ansökningar" },
  excerpt: "Personlig medarbetarberättelse som blev rekryteringsmotor i Norrlands hårdaste arbetsmarknad.",
  scope: ["Story & manus", "Regi & foto", "Klipp & grade", "Versionering social"],
  challenge: "Garageportexperten behövde rekrytera servicetekniker i en region där alla slåss om samma fåtal händer. Klassisk rekryteringsannons skulle drunkna.",
  solution: "Vi följde Maria — en av de befintliga teknikerna — en hel arbetsdag. Ingen manuskonst, ingen reklamröst. Bara en människa som berättar varför hon stannat i 11 år. Filmen klipptes i ett långformat för sajt + sex korta versioner för LinkedIn, Instagram och Meta Ads.",
  outcome: "Filmen blev företagets mest delade content någonsin. Ansökningarna ökade med 38 % på 90 dagar och två rekryteringar gick i mål utan extern byråkostnad.",
  metrics: [
    { value: "+38%", label: "fler ansökningar (90 dgr)" },
    { value: "287k", label: "organiska views" },
    { value: "6 cuts", label: "från 1 produktion" },
  ],
  quote: {
    text: "Det är första gången en film om oss känns som oss. Folk ringer och säger att de såg den.",
    name: "Maria Borg",
    role: "Marknadschef, Garageportexperten",
  },
  gallery: ["KkE8BqgQ14M", "O2zdqLfmM5E"],
};

export const caseItems: CaseDetail[] = [
  {
    id: "KkE8BqgQ14M",
    slug: "garageportexperten-30-ar",
    title: "30 år med Garageportexperten",
    client: "Garageportexperten",
    category: "Brand",
    service: "Prime",
    format: "Dokumentär · 16:9",
    industry: "Industri",
    year: "2024",
    metric: { value: "287k", label: "organiska views" },
    excerpt: "Anniversary-film som blev rörelsens hjärtljud — visad på event, sajt och sociala.",
    scope: ["Research & intervjuer", "Regi", "Foto & ljud", "Klipp"],
    challenge: "Ett 30-årsjubileum riskerar alltid att bli en intern hyllningsfilm. Vi skulle göra något som faktiskt rörde både kunder, anställda och nya rekryter.",
    solution: "Vi intervjuade grundare, tekniker och kunder och vävde ihop berättelsen som en dokumentär — inte en reklamfilm. Premiärvisades på jubileumsfest, släpptes sedan publikt.",
    outcome: "287k organiska views, plockades upp av lokala medier och blev internt rekryteringsverktyg i ett år efter premiär.",
    metrics: [
      { value: "287k", label: "organiska views" },
      { value: "12 min", label: "snittvisningstid på event" },
      { value: "0 kr", label: "betald distribution" },
    ],
  },
  {
    id: "UU_Ys1M431I",
    slug: "arets-pryl-2026",
    seoDescription:
      "Snabb kampanjfilm för Årets Pryl 2026 med tempo som matchade säsongens trafiktopp — 12x ROAS i Q4. Se upplägg, produktionstid och resultat i caset.",
    title: "Årets Pryl 2026",
    client: "Årets Pryl",
    category: "Kampanj",
    service: "Prime",
    format: "Kampanjfilm · 16:9",
    industry: "Konsument",
    year: "2026",
    metric: { value: "12x", label: "ROAS i Q4" },
    excerpt: "Snabb kampanjfilm med tempo som matchade säsongens trafiktopp.",
    scope: ["Koncept", "Produktion", "Versionering", "Paid-anpassning"],
    challenge: "Lansering inför Q4-säsong. Tre veckor från brief till live. Måste fungera både på TV-yta och social.",
    solution: "Stram kampanjidé byggd runt en hook som funkade både i 30s och 9:16. Vi sköt allt på en dag och levererade sju versioner.",
    outcome: "12x ROAS i Q4, lägsta CPM kampanjen någonsin haft.",
    metrics: [
      { value: "12x", label: "ROAS Q4" },
      { value: "-43%", label: "CPM vs föregående år" },
      { value: "7 cuts", label: "från 1 inspelningsdag" },
    ],
  },
  {
    id: "HwWGVOl6dtg",
    slug: "huawei-also-energy",
    title: "Huawei Energy Solution in Denmark",
    client: "ALSO / Huawei",
    category: "Brand",
    service: "Prime",
    format: "B2B brandfilm · 16:9",
    industry: "Energi",
    year: "2024",
    metric: { value: "4 marknader", label: "samma asset" },
    excerpt: "B2B-brandfilm i flera språkversioner — en produktion, fyra distributionsspår.",
    scope: ["Story", "Produktion på plats i DK", "Klipp & grade", "Språkversioner"],
    challenge: "B2B-energifilm som skulle funka på fyra olika nordiska marknader utan att kännas generisk.",
    solution: "Vi byggde en stomme på engelska och planerade in tomrum för voice-over på lokala språk. Bilden bär storyn — texten anpassar tonen.",
    outcome: "En film blev fyra. Sparade ~60 % jämfört med att producera per marknad.",
    metrics: [
      { value: "4", label: "språkversioner" },
      { value: "-60%", label: "kostnad vs alt." },
      { value: "B2B", label: "Energi & Industri" },
    ],
  },
  {
    id: "yvojXrb41Aw",
    slug: "bolt-20-sekunder",
    seoDescription:
      "20-sekunders kampanjfilm för Bolt med hook på tre sekunder och 47 procent högre CTR än benchmark. Se hur stoppkraft byggs in redan i manus och klipp.",
    title: "Bolt — 20-sekunders kampanj",
    client: "Bolt",
    category: "Reklam",
    service: "Pulse",
    format: "Performance · 9:16",
    industry: "Mobility",
    year: "2025",
    metric: { value: "+47%", label: "CTR vs benchmark" },
    excerpt: "Stoppkraft + hook på 3 sek = paid social som faktiskt presterar.",
    scope: ["Hook-koncept", "Produktion 1 dag", "9:16 + 1:1 cuts"],
    challenge: "Paid social mättnad. Befintligt creative tröttnade ut på en vecka.",
    solution: "Tre olika hooks, samma produktion. A/B-test direkt i ads manager.",
    outcome: "+47 % CTR mot tidigare bästa creative. Vinnande hook skalades till tre marknader.",
    metrics: [
      { value: "+47%", label: "CTR vs benchmark" },
      { value: "3 hooks", label: "från 1 dag" },
      { value: "3 marknader", label: "skalning" },
    ],
  },
  {
    id: "WONrMkeJuik",
    slug: "wimeck-detailing",
    seoDescription:
      "Se hur Fenny hjälpte Wimeck Detailing att kombinera varumärke och försäljning i en och samma film — och öka bokningarna online med 62 procent på tre månader.",
    title: "Wimeck Detailing — Allt på en plats",
    client: "Wimeck",
    category: "Brand",
    service: "Pulse",
    format: "Tjänstefilm · 16:9",
    industry: "Konsument",
    year: "2025",
    metric: { value: "+62%", label: "bokningar online" },
    excerpt: "Brand + sälj i samma film — utan att kompromissa på någondera.",
    scope: ["Story", "Produktion", "Klipp", "Versionering"],
    challenge: "Existerande material kändes som reklam. Wimeck ville berätta vad de gör utan att tappa hantverkskänslan.",
    solution: "Vi gjorde en film där hantverket bär — bilden visar, rösten guidar. CTA i slutet, inte i tonen.",
    outcome: "+62 % onlinebokningar tre månader efter lansering.",
    metrics: [
      { value: "+62%", label: "bokningar online" },
      { value: "3 mån", label: "till mätbart utfall" },
    ],
  },
  {
    id: "O2zdqLfmM5E",
    slug: "garageportexperten-hemma",
    seoDescription:
      "Löpande kampanjspår för Garageportexperten där varje månads film bygger vidare på föregående. Se hur åtta månaders filmflöde planerades, spelades in och mättes.",
    title: "Garageportexperten Hemma",
    client: "Garageportexperten",
    category: "Kampanj",
    service: "Pulse",
    format: "Kampanj · 16:9",
    industry: "Industri",
    year: "2024",
    metric: { value: "8 mån", label: "löpande kampanjspår" },
    excerpt: "Långsiktigt kampanjspår där varje månads film bygger på föregående.",
    scope: ["Konceptlinje", "Månadsleveranser", "Säsongsanpassning"],
    challenge: "Behov av kontinuerlig närvaro utan att tappa varumärkesnivån.",
    solution: "Vi byggde ett konceptspår med åtta filmer — en per månad — där varje del kunde leva för sig men också stärka helheten.",
    outcome: "Konsekvent närvaro hela året, ökad varumärkeskännedom mätt i tracking.",
    metrics: [
      { value: "8", label: "filmer / 8 mån" },
      { value: "+19%", label: "brand awareness lift" },
    ],
  },
];

export const heroStats = [
  { value: 247, suffix: "+", label: "filmer levererade" },
  { value: 12, suffix: "M+", label: "totala views" },
  { value: 38, suffix: "", label: "nöjda kunder" },
  { value: 4, suffix: " år", label: "längsta löpande relation" },
];

export const industries = ["Alla branscher", "Industri", "Mobility", "Energi", "Konsument", "B2B SaaS"] as const;

export function getCaseBySlug(slug: string): CaseDetail | undefined {
  if (featuredCase.slug === slug) return featuredCase;
  return caseItems.find((c) => c.slug === slug);
}

export function getRelatedCases(slug: string, industry?: string, limit = 3): CaseDetail[] {
  const all = [featuredCase, ...caseItems].filter((c) => c.slug !== slug);
  const sameIndustry = industry ? all.filter((c) => c.industry === industry) : [];
  const rest = all.filter((c) => !sameIndustry.includes(c));
  return [...sameIndustry, ...rest].slice(0, limit);
}
